// frontend/src/pages/company/CompanySettlements.jsx
import { useState, useEffect } from 'react';
import { settlementAPI, codSettlementAPI } from '../../services/api';
import CompanyLayout from '../../components/layout/CompanyLayout';

const CompanySettlements = () => {
  // Tab state - 4 tabs
  const [activeTab, setActiveTab] = useState('shops');

  // ============================================
  // ШОПЫН ТООЦОО STATE (Tab 1)
  // ============================================
  const [shopSettlements, setShopSettlements] = useState([]);
  const [shopSummary, setShopSummary] = useState(null);
  const [shopLoading, setShopLoading] = useState(false);
  const [filterInfo, setFilterInfo] = useState(null);
  const [selectedShop, setSelectedShop] = useState('all');
  const [dateRange, setDateRange] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [shops, setShops] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedShopData, setSelectedShopData] = useState(null);
  const [shopDetail, setShopDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    payment_reference: '',
    notes: ''
  });
  const [paymentLoading, setPaymentLoading] = useState(false);

  // ============================================
  // COD ТООЦОО STATE (Tab 2)
  // ============================================
  const [codData, setCodData] = useState(null);
  const [codLoading, setCodLoading] = useState(false);
  const [codDateRange, setCodDateRange] = useState('month');
  const [codShopFilter, setCodShopFilter] = useState('all');
  const [codCustomStart, setCodCustomStart] = useState('');
  const [codCustomEnd, setCodCustomEnd] = useState('');
  const [showCodPaymentModal, setShowCodPaymentModal] = useState(false);
  const [selectedCodItem, setSelectedCodItem] = useState(null);
  const [codPaymentForm, setCodPaymentForm] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    payment_reference: '',
    notes: ''
  });
  const [codPaymentLoading, setCodPaymentLoading] = useState(false);

   // COD Payment History State
  const [codPaymentHistory, setCodPaymentHistory] = useState([]);
  const [codPaymentHistoryLoading, setCodPaymentHistoryLoading] = useState(false);

  // ============================================
  // ЖОЛООЧИЙН ТООЦОО STATE (Tab 3)
  // ============================================
  const [driverCodData, setDriverCodData] = useState(null);
  const [driverCodLoading, setDriverCodLoading] = useState(false);
  const [driverDateRange, setDriverDateRange] = useState('month');
  const [driverFilter, setDriverFilter] = useState('all');
  const [driverCustomStart, setDriverCustomStart] = useState('');
  const [driverCustomEnd, setDriverCustomEnd] = useState('');
  const [showDriverTransferModal, setShowDriverTransferModal] = useState(false);
  const [selectedDriverItem, setSelectedDriverItem] = useState(null);
  const [driverTransferForm, setDriverTransferForm] = useState({
    amount: '',
    transfer_type: 'cod',  // 'cod' эсвэл 'delivery_fee'
    transfer_method: 'cash',
    transfer_reference: '',
    notes: ''
  });
  const [driverTransferLoading, setDriverTransferLoading] = useState(false);

  // ============================================
  // СИСТЕМИЙН ШИМТГЭЛ STATE (Tab 4)
  // ============================================
  const [commissionData, setCommissionData] = useState(null);
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [commissionDateRange, setCommissionDateRange] = useState('month');
  const [commissionCustomStart, setCommissionCustomStart] = useState('');
  const [commissionCustomEnd, setCommissionCustomEnd] = useState('');

  // ============================================
  // FORMAT HELPERS
  // ============================================
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('mn-MN').format(amount || 0) + '₮';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('mn-MN');
  };

  const formatPercent = (success, total) => {
    if (!total || total === 0) return '0%';
    return Math.round((success / total) * 100) + '%';
  };

  const getDateRangeLabel = (range) => {
    switch (range) {
      case 'today': return 'Өнөөдөр';
      case 'yesterday': return 'Өчигдөр';
      case 'week': return 'Сүүлийн 7 хоног';
      case 'month': return 'Сүүлийн 30 хоног';
      case 'custom': return 'Сонгосон хугацаа';
      default: return 'Сүүлийн 30 хоног';
    }
  };

  // ============================================
  // DATA FETCH - ШОПЫН ТООЦОО (Tab 1)
  // ============================================
  const fetchShopSettlements = async () => {
    setShopLoading(true);
    try {
      const params = { date_range: dateRange };
      if (selectedShop !== 'all') params.shop_id = selectedShop;
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
      const response = await settlementAPI.getShopSettlements(params);
      if (response.data.success) {
        setShopSettlements(response.data.data.settlements);
        setShopSummary(response.data.data.summary);
        setFilterInfo(response.data.data.filter);
        if (shops.length === 0) {
          const shopList = response.data.data.settlements.map(s => ({
            id: s.shop.id,
            name: s.shop.name
          }));
          setShops(shopList);
        }
      }
    } catch (error) {
      console.error('Shop settlements fetch error:', error);
    } finally {
      setShopLoading(false);
    }
  };

  // ============================================
  // DATA FETCH - COD ТООЦОО (Tab 2)
  // ============================================
  const fetchCodSettlements = async () => {
    setCodLoading(true);
    try {
      const params = { date_range: codDateRange };
      if (codShopFilter !== 'all') params.shop_id = codShopFilter;
      if (codDateRange === 'custom' && codCustomStart && codCustomEnd) {
        params.start_date = codCustomStart;
        params.end_date = codCustomEnd;
      }
      const response = await codSettlementAPI.getCodSettlements(params);
       if (response.data.success) {
        setCodData(response.data.data);
      }
      
      // Төлбөрийн түүх татах
      const historyResponse = await codSettlementAPI.getCodPayments(params);
      if (historyResponse.data.success) {
        setCodPaymentHistory(historyResponse.data.data.payments || []);
      }
    } catch (error) {
      console.error('COD settlements fetch error:', error);
    } finally {
      setCodLoading(false);
    }
  };

  // ============================================
  // DATA FETCH - ЖОЛООЧИЙН ТООЦОО (Tab 3)
  // ============================================
  const fetchDriverCodSettlements = async () => {
    setDriverCodLoading(true);
    try {
      const params = { date_range: driverDateRange };
      if (driverFilter !== 'all') params.driver_id = driverFilter;
      if (driverDateRange === 'custom' && driverCustomStart && driverCustomEnd) {
        params.start_date = driverCustomStart;
        params.end_date = driverCustomEnd;
      }
      const response = await codSettlementAPI.getDriverCodSettlements(params);
      if (response.data.success) {
        console.log('Driver COD Data:', response.data.data);
        console.log('Transfer History:', response.data.data.transfer_history);
        setDriverCodData(response.data.data);
      }
    } catch (error) {
      console.error('Driver COD fetch error:', error);
    } finally {
      setDriverCodLoading(false);
    }
  };

  // ============================================
  // DATA FETCH - СИСТЕМИЙН ШИМТГЭЛ (Tab 4)
  // ============================================
  const fetchCommissionData = async () => {
    setCommissionLoading(true);
    try {
      const params = { date_range: commissionDateRange };
      if (commissionDateRange === 'custom' && commissionCustomStart && commissionCustomEnd) {
        params.start_date = commissionCustomStart;
        params.end_date = commissionCustomEnd;
      }
      const response = await settlementAPI.getCompanyCommission(params);
      if (response.data.success) {
        setCommissionData(response.data.data);
      }
    } catch (error) {
      console.error('Commission fetch error:', error);
    } finally {
      setCommissionLoading(false);
    }
  };

  // ============================================
  // USEEFFECT - TAB CHANGE
  // ============================================
  useEffect(() => {
    if (activeTab === 'shops') {
      fetchShopSettlements();
    } else if (activeTab === 'cod') {
      fetchCodSettlements();
    } else if (activeTab === 'driver') {
      fetchDriverCodSettlements();
    } else if (activeTab === 'commission') {
      fetchCommissionData();
    }
  }, [activeTab]);

  // ============================================
  // HANDLERS - ШОПЫН ТООЦОО
  // ============================================
  const handleViewShopDetail = async (shopData) => {
    setSelectedShopData(shopData);
    setShowDetailModal(true);
    setDetailLoading(true);
    try {
      const params = { date_range: dateRange };
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
      const response = await settlementAPI.getShopSettlementDetail(shopData.shop.id, params);
      if (response.data.success) {
        setShopDetail(response.data.data);
      }
    } catch (error) {
      console.error('Shop detail fetch error:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOpenPayment = (shopData) => {
    setSelectedShopData(shopData);
    setPaymentForm({
      amount: shopData.balance.balance.toString(),
      payment_method: 'cash',
      payment_reference: '',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      alert('Төлбөрийн дүн оруулна уу');
      return;
    }
    setPaymentLoading(true);
    try {
      const response = await settlementAPI.recordShopPayment(selectedShopData.shop.id, {
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        payment_reference: paymentForm.payment_reference,
        notes: paymentForm.notes
      });
      if (response.data.success) {
        alert('Төлбөр амжилттай бүртгэгдлээ');
        setShowPaymentModal(false);
        fetchShopSettlements();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Төлбөр бүртгэхэд алдаа гарлаа');
    } finally {
      setPaymentLoading(false);
    }
  };

  // ============================================
  // HANDLERS - COD ТӨЛБӨР БҮРТГЭХ
  // ============================================
 const handleOpenCodPayment = (item) => {
    setSelectedCodItem(item);
    const balance = item.balance || item.total_cod || 0;
    setCodPaymentForm({
      amount: balance > 0 ? balance.toString() : '',
      payment_method: 'bank_transfer',
      payment_reference: '',
      notes: ''
    });
    setShowCodPaymentModal(true);
  };

  const handleRecordCodPayment = async (e) => {
    e.preventDefault();
    
    // Үлдэгдэл шалгах
    const remainingBalance = selectedCodItem.balance || 0;
    if (remainingBalance <= 0) {
      alert('Төлбөр аль хэдийн бүртгэгдсэн эсвэл хүлээгдэж байна');
      return;
    }
    
    if (!codPaymentForm.amount || parseFloat(codPaymentForm.amount) <= 0) {
      alert('Төлбөрийн дүн оруулна уу');
      return;
    }
    
    // Үлдэгдлээс их төлбөр бүртгэхгүй
    if (parseFloat(codPaymentForm.amount) > remainingBalance) {
      alert(`Төлбөрийн дүн үлдэгдлээс их байна. Төлөх үлдэгдэл: ${formatCurrency(remainingBalance)}`);
      return;
    }
    setCodPaymentLoading(true);
    try {
      // Шинэ систем - createCodPayment (pending төлөвтэй)
      const response = await codSettlementAPI.createCodPayment({
        shop_id: selectedCodItem.shop?.id || selectedCodItem.shop_id,
        amount: parseFloat(codPaymentForm.amount),
        payment_method: codPaymentForm.payment_method,
        payment_reference: codPaymentForm.payment_reference,
        notes: codPaymentForm.notes,
        delivery_date_from: selectedCodItem.delivery_date,
        delivery_date_to: selectedCodItem.delivery_date
      });
      if (response.data.success) {
        alert('Төлбөр бүртгэгдлээ. Шоп баталгаажуулахыг хүлээж байна.');
        setShowCodPaymentModal(false);
        fetchCodSettlements();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Төлбөр бүртгэхэд алдаа гарлаа');
    } finally {
      setCodPaymentLoading(false);
    }
  };

  // ============================================
  // HANDLERS - ЖОЛООЧИЙН ШИЛЖҮҮЛЭГ
  // ============================================
  const handleOpenDriverTransfer = (item) => {
    setSelectedDriverItem(item);
    const codBalance = item.balance || 0;
    setDriverTransferForm({
      amount: codBalance > 0 ? codBalance.toString() : '',
      transfer_type: 'cod',
      transfer_method: 'cash',
      transfer_reference: '',
      notes: ''
    });
    setShowDriverTransferModal(true);
  };

 const handleRecordDriverTransfer = async (e) => {
    e.preventDefault();
    
    const isCod = driverTransferForm.transfer_type === 'cod';
    const isDeliveryFee = driverTransferForm.transfer_type === 'delivery_fee';
    
    // Үлдэгдэл шалгах - төрлөөс хамаарч
    const codBalance = selectedDriverItem.balance || 0;
    const deliveryFeeBalance = selectedDriverItem.delivery_fee_balance || selectedDriverItem.collected_delivery_fee || 0;
    const remainingBalance = isCod ? codBalance : deliveryFeeBalance;
    
    if (remainingBalance <= 0) {
      alert(isCod ? 'COD бүрэн шилжүүлэгдсэн' : 'Хүргэлтийн төлбөр бүрэн авсан');
      return;
    }
    
    if (!driverTransferForm.amount || parseFloat(driverTransferForm.amount) <= 0) {
      alert('Дүн оруулна уу');
      return;
    }
    
    // Үлдэгдлээс их шилжүүлэхгүй
    if (parseFloat(driverTransferForm.amount) > remainingBalance) {
      alert(`Авах боломжтой дүн: ${formatCurrency(remainingBalance)}`);
      return;
    }
    
    console.log('selectedDriverItem:', selectedDriverItem);
    console.log('transfer_type:', driverTransferForm.transfer_type);
    
    setDriverTransferLoading(true);
    try {
      const response = await codSettlementAPI.recordDriverTransfer({
        driver_id: selectedDriverItem.assigned_driver_id || selectedDriverItem.driver_id,
        delivery_date: selectedDriverItem.delivery_date,
        amount: parseFloat(driverTransferForm.amount),
        transfer_type: driverTransferForm.transfer_type,
        transfer_method: driverTransferForm.transfer_method,
        transfer_reference: driverTransferForm.transfer_reference,
        notes: driverTransferForm.notes
      });
      if (response.data.success) {
        alert(response.data.message || 'Амжилттай бүртгэгдлээ');
        setShowDriverTransferModal(false);
        fetchDriverCodSettlements();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Бүртгэхэд алдаа гарлаа');
    } finally {
      setDriverTransferLoading(false);
    }
  };
  // ============================================
  // RENDER
  // ============================================
  return (
    <CompanyLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* TAB NAVIGATION - 4 tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1">
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => setActiveTab('shops')}
              className={`flex-1 sm:flex-none px-3 sm:px-5 py-2.5 rounded-lg font-medium transition-all text-xs sm:text-sm ${
                activeTab === 'shops'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <span>🏪</span>
                <span className="hidden sm:inline">Шопын тооцоо</span>
                <span className="sm:hidden">Шоп</span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('cod')}
              className={`flex-1 sm:flex-none px-3 sm:px-5 py-2.5 rounded-lg font-medium transition-all text-xs sm:text-sm ${
                activeTab === 'cod'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <span>💵</span>
                <span className="hidden sm:inline">COD тооцоо</span>
                <span className="sm:hidden">COD</span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('driver')}
              className={`flex-1 sm:flex-none px-3 sm:px-5 py-2.5 rounded-lg font-medium transition-all text-xs sm:text-sm ${
                activeTab === 'driver'
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <span>🚗</span>
                <span className="hidden sm:inline">Жолоочийн тооцоо</span>
                <span className="sm:hidden">Жолооч</span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('commission')}
              className={`flex-1 sm:flex-none px-3 sm:px-5 py-2.5 rounded-lg font-medium transition-all text-xs sm:text-sm ${
                activeTab === 'commission'
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <span>💸</span>
                <span className="hidden sm:inline">Системийн шимтгэл</span>
                <span className="sm:hidden">Шимтгэл</span>
              </span>
            </button>
          </div>
        </div>

        {/* ============================================ */}
        {/* TAB 1: ШОПЫН ТООЦОО */}
        {/* ============================================ */}
        {activeTab === 'shops' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Filter */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Онлайн шоп</label>
                  <select value={selectedShop} onChange={(e) => setSelectedShop(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
                    <option value="all">Бүгд ({shops.length})</option>
                    {shops.map(shop => (
                      <option key={shop.id} value={shop.id}>{shop.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Хугацаа</label>
                  <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
                    <option value="today">Өнөөдөр</option>
                    <option value="yesterday">Өчигдөр</option>
                    <option value="week">Сүүлийн 7 хоног</option>
                    <option value="month">Сүүлийн 30 хоног</option>
                    <option value="custom">Хугацаа сонгох</option>
                  </select>
                </div>
                {dateRange === 'custom' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Эхлэх</label>
                      <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Дуусах</label>
                      <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </>
                )}
                <div className={`flex items-end ${dateRange === 'custom' ? 'lg:col-span-4' : ''}`}>
                  <button onClick={fetchShopSettlements} disabled={shopLoading}
                    className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium">
                    {shopLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : '🔍'}
                    <span>Хайх</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Summary Cards - Tab 1 */}
            {shopSummary && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 sm:p-5 text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-blue-100 text-xs sm:text-sm">Нийт шоп</p>
                      <p className="text-2xl sm:text-3xl font-bold mt-1">{shopSummary.total_shops}</p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <span className="text-xl">🏪</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 sm:p-5 text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-green-100 text-xs sm:text-sm">Хүргэлт</p>
                      <p className="text-2xl sm:text-3xl font-bold mt-1">
                        {shopSummary.period_successful || 0}/{shopSummary.period_total_deliveries || 0}
                      </p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <span className="text-xl">📦</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 sm:p-5 text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-purple-100 text-xs sm:text-sm">Нийт төлбөр</p>
                      <p className="text-xl sm:text-2xl font-bold mt-1 truncate">{formatCurrency(shopSummary.period_total_fee)}</p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <span className="text-xl">💵</span>
                    </div>
                  </div>
                </div>
                <div className={`bg-gradient-to-br rounded-xl p-4 sm:p-5 text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer ${
                  shopSummary.total_balance > 0 ? 'from-orange-500 to-orange-600' : 'from-emerald-500 to-emerald-600'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs sm:text-sm ${shopSummary.total_balance > 0 ? 'text-orange-100' : 'text-emerald-100'}`}>Үлдэгдэл</p>
                      <p className="text-xl sm:text-2xl font-bold mt-1 truncate">{formatCurrency(shopSummary.total_balance)}</p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <span className="text-xl">{shopSummary.total_balance > 0 ? '💰' : '✅'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Table - Tab 1 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Шопуудын жагсаалт</h3>
                <span className="text-sm text-gray-500">{shopSettlements.length} шоп</span>
              </div>
              {shopLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Шоп</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Хүргэлт</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Төлбөр</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Үлдэгдэл</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Үйлдэл</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {shopSettlements.length === 0 ? (
                        <tr><td colSpan="5" className="px-6 py-12 text-center text-gray-500">📭 Мэдээлэл байхгүй</td></tr>
                      ) : (
                        shopSettlements.map((item) => {
                          const stats = item.period || item.today || {};
                          return (
                            <tr key={item.shop.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900 text-sm">{item.shop.name}</div>
                                <div className="text-xs text-gray-500">{item.shop.phone}</div>
                              </td>
                              <td className="px-4 py-3 text-center hidden sm:table-cell">
                                <span className="text-green-600 font-medium text-sm">{stats.successful}</span>
                                <span className="text-gray-400 text-sm">/{stats.total_deliveries}</span>
                              </td>
                              <td className="px-4 py-3 text-right hidden sm:table-cell">
                                <span className="font-medium text-gray-900 text-sm">{formatCurrency(stats.total_fee)}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className={`font-semibold text-sm ${item.balance.balance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                  {formatCurrency(item.balance.balance)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-1">
                                  <button onClick={() => handleViewShopDetail(item)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                  </button>
                                  {item.balance.balance > 0 && (
                                    <button onClick={() => handleOpenPayment(item)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* TAB 2: COD ТООЦОО */}
        {/* ============================================ */}
        {activeTab === 'cod' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Filter */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Шоп</label>
                  <select value={codShopFilter} onChange={(e) => setCodShopFilter(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm">
                    <option value="all">Бүгд ({codData?.shops?.length || 0})</option>
                    {codData?.shops?.map(shop => (
                      <option key={shop.id} value={shop.id}>{shop.shop_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Хугацаа</label>
                  <select value={codDateRange} onChange={(e) => setCodDateRange(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm">
                    <option value="today">Өнөөдөр</option>
                    <option value="yesterday">Өчигдөр</option>
                    <option value="week">7 хоног</option>
                    <option value="month">30 хоног</option>
                    <option value="custom">Сонгох</option>
                  </select>
                </div>
                {codDateRange === 'custom' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Эхлэх</label>
                      <input type="date" value={codCustomStart} onChange={(e) => setCodCustomStart(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Дуусах</label>
                      <input type="date" value={codCustomEnd} onChange={(e) => setCodCustomEnd(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </>
                )}
                <div className="flex items-end">
                  <button onClick={fetchCodSettlements} disabled={codLoading}
                    className="w-full sm:w-auto px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium">
                    {codLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : '🔍'}
                    <span>Хайх</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Summary Cards - Tab 2 */}
            {codData?.summary && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 sm:p-5 text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-indigo-100 text-xs sm:text-sm">Нийт хүргэлт</p>
                      <p className="text-2xl sm:text-3xl font-bold mt-1">{codData.summary.delivered_count || 0}</p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <span className="text-xl">📦</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl p-4 sm:p-5 text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-violet-100 text-xs sm:text-sm">Нийт COD</p>
                      <p className="text-xl sm:text-2xl font-bold mt-1 truncate">{formatCurrency(codData.summary.total_cod)}</p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <span className="text-xl">💵</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 sm:p-5 text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-teal-100 text-xs sm:text-sm">Төлсөн</p>
                      <p className="text-xl sm:text-2xl font-bold mt-1 truncate">{formatCurrency(codData.summary.paid_amount)}</p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <span className="text-xl">✅</span>
                    </div>
                  </div>
                </div>
                <div className={`bg-gradient-to-br rounded-xl p-4 sm:p-5 text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer ${
                  codData.summary.balance > 0 ? 'from-rose-500 to-rose-600' : 'from-emerald-500 to-emerald-600'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs sm:text-sm ${codData.summary.balance > 0 ? 'text-rose-100' : 'text-emerald-100'}`}>Төлөх үлдэгдэл</p>
                      <p className="text-xl sm:text-2xl font-bold mt-1 truncate">{formatCurrency(codData.summary.balance)}</p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <span className="text-xl">{codData.summary.balance > 0 ? '💰' : '🎉'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Table - Tab 2 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">📋 COD тооцооны жагсаалт</h3>
                <span className="text-sm text-gray-500">{codData?.daily_stats?.length || 0} бичлэг</span>
              </div>
              {codLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Огноо</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Шоп</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Хүргэлт</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Нийт COD</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Үйлдэл</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {!codData?.daily_stats || codData.daily_stats.length === 0 ? (
                        <tr><td colSpan="5" className="px-6 py-12 text-center text-gray-500">📭 Мэдээлэл байхгүй</td></tr>
                      ) : (
                        codData.daily_stats.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{formatDate(item.delivery_date)}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.shop?.shop_name || '-'}</td>
                            <td className="px-4 py-3 text-sm text-center text-gray-600 hidden sm:table-cell">{item.total_deliveries || 0}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{formatCurrency(item.total_cod)}</td>
                            <td className="px-4 py-3 text-center">
                              <button onClick={() => handleOpenCodPayment(item)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg" title="Төлбөр бүртгэх">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
              </div>
              )}
            </div>

            {/* Төлбөрийн түүх */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">📜 Төлбөрийн түүх</h3>
                <span className="text-sm text-gray-500">{codPaymentHistory?.length || 0} бичлэг</span>
              </div>
              <div className="divide-y divide-gray-200 max-h-[300px] overflow-y-auto">
                {!codPaymentHistory || codPaymentHistory.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <span className="text-3xl">📭</span>
                    <p className="mt-2">Төлбөрийн түүх байхгүй</p>
                  </div>
                ) : (
                  codPaymentHistory.map((payment) => (
                    <div key={payment.id} className="p-3 sm:p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          payment.status === 'confirmed' ? 'bg-green-100' : 
                          payment.status === 'rejected' ? 'bg-red-100' : 'bg-yellow-100'
                        }`}>
                          {payment.status === 'confirmed' ? (
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : payment.status === 'rejected' ? (
                            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{payment.shop?.shop_name || 'Шоп'}</p>
                          <p className="text-xs text-gray-500">{formatDate(payment.payment_date)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{formatCurrency(payment.amount)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        payment.status === 'confirmed' ? 'bg-green-100 text-green-700' : 
                        payment.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {payment.status === 'confirmed' ? 'Баталгаажсан' : 
                         payment.status === 'rejected' ? 'Татгалзсан' : 'Хүлээгдэж байна'}
                      </span>
                      {payment.notes && (
                        <p className="text-xs text-gray-500 mt-1">💬 {payment.notes}</p>
                      )}
                      {payment.rejection_notes && (
                        <p className="text-xs text-red-500 mt-1">❌ {payment.rejection_notes}</p>
                      )}
                    </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* TAB 3: ЖОЛООЧИЙН ТООЦОО */}
        {/* ============================================ */}
        {activeTab === 'driver' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Filter */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Жолооч</label>
                  <select value={driverFilter} onChange={(e) => setDriverFilter(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm">
                    <option value="all">Бүгд ({driverCodData?.drivers?.length || 0})</option>
                    {driverCodData?.drivers?.map(driver => (
                      <option key={driver.id} value={driver.id}>{driver.driver_name} - {driver.phone}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Хугацаа</label>
                  <select value={driverDateRange} onChange={(e) => setDriverDateRange(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm">
                    <option value="today">Өнөөдөр</option>
                    <option value="yesterday">Өчигдөр</option>
                    <option value="week">7 хоног</option>
                    <option value="month">30 хоног</option>
                    <option value="custom">Сонгох</option>
                  </select>
                </div>
                {driverDateRange === 'custom' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Эхлэх</label>
                      <input type="date" value={driverCustomStart} onChange={(e) => setDriverCustomStart(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Дуусах</label>
                      <input type="date" value={driverCustomEnd} onChange={(e) => setDriverCustomEnd(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </>
                )}
                <div className="flex items-end">
                  <button onClick={fetchDriverCodSettlements} disabled={driverCodLoading}
                    className="w-full sm:w-auto px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium">
                    {driverCodLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : '🔍'}
                    <span>Хайх</span>
                  </button>
                </div>
              </div>
            </div>

             {/* Summary Cards - Tab 3 */}
            {driverCodData?.summary && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {/* Нийт хүргэлт */}
                <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl p-4 text-white shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-cyan-100 text-xs">Нийт хүргэлт</p>
                      <p className="text-2xl font-bold">{driverCodData.summary.delivered_count || 0}</p>
                    </div>
                    <span className="text-2xl">📦</span>
                  </div>
                </div>
                {/* COD авсан */}
                <div className="bg-gradient-to-br from-sky-500 to-sky-600 rounded-xl p-4 text-white shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sky-100 text-xs">COD авсан</p>
                      <p className="text-xl font-bold truncate">{formatCurrency(driverCodData.summary.total_collected_cod || 0)}</p>
                    </div>
                    <span className="text-2xl">💵</span>
                  </div>
                </div>
                {/* Хүргэлт авсан */}
                <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-indigo-100 text-xs">Хүргэлт авсан</p>
                      <p className="text-xl font-bold truncate">{formatCurrency(driverCodData.summary.total_collected_delivery_fee || 0)}</p>
                    </div>
                    <span className="text-2xl">🚚</span>
                  </div>
                </div>
                {/* Шилжүүлсэн */}
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-xs">Шилжүүлсэн</p>
                      <p className="text-xl font-bold truncate">{formatCurrency(driverCodData.summary.total_transferred || 0)}</p>
                    </div>
                    <span className="text-2xl">✅</span>
                  </div>
                </div>
                {/* Авах үлдэгдэл */}
                <div className={`bg-gradient-to-br rounded-xl p-4 text-white shadow-lg ${
                  (driverCodData.summary.balance || 0) > 0 ? 'from-amber-500 to-amber-600' : 'from-emerald-500 to-emerald-600'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-xs ${(driverCodData.summary.balance || 0) > 0 ? 'text-amber-100' : 'text-emerald-100'}`}>Авах үлдэгдэл</p>
                      <p className="text-xl font-bold truncate">{formatCurrency(driverCodData.summary.balance || 0)}</p>
                    </div>
                    <span className="text-2xl">{(driverCodData.summary.balance || 0) > 0 ? '⚠️' : '🎉'}</span>
                  </div>
                </div>
              </div>
            )}
            {/* Table - Tab 3 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">🚗 Жолоочийн тооцоо</h3>
                <span className="text-sm text-gray-500">{driverCodData?.driver_stats?.length || 0} бичлэг</span>
              </div>
              {driverCodLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Огноо</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Жолооч</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Хүргэлт</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">COD</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Хүргэлт</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Нийт</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Үйлдэл</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {!driverCodData?.driver_stats || driverCodData.driver_stats.length === 0 ? (
                        <tr><td colSpan="5" className="px-6 py-12 text-center text-gray-500">📭 Мэдээлэл байхгүй</td></tr>
                      ) : (
                        driverCodData.driver_stats.map((item, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{formatDate(item.delivery_date)}</td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900 text-sm">{item.driver_name || '-'}</div>
                              <div className="text-xs text-gray-500">{item.phone}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-center text-gray-600 hidden sm:table-cell">{item.total_deliveries || 0}</td>
                            <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(item.collected_amount || 0)}</td>
                            <td className="px-4 py-3 text-sm text-right font-medium text-indigo-600 hidden md:table-cell">{formatCurrency(item.collected_delivery_fee || 0)}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold text-emerald-600">{formatCurrency(item.total_collected || 0)}</td>
                            <td className="px-4 py-3 text-center">
                              <button onClick={() => handleOpenDriverTransfer(item)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Шилжүүлэг бүртгэх">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
             </div>

            {/* Шилжүүлгийн түүх */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">📜 Шилжүүлгийн түүх</h3>
                <span className="text-sm text-gray-500">{driverCodData?.transfer_history?.length || 0} бичлэг</span>
              </div>
              <div className="divide-y divide-gray-200 max-h-[300px] overflow-y-auto">
                {!driverCodData?.transfer_history || driverCodData.transfer_history.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <span className="text-3xl">📭</span>
                    <p className="mt-2">Шилжүүлгийн түүх байхгүй</p>
                  </div>
                ) : (
                  driverCodData.transfer_history.map((transfer) => (
                    <div key={transfer.id} className="p-3 sm:p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                          <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{transfer.driverSettlement?.driver?.driver_name || transfer.created_by_name || 'Жолооч'}</p>
                          <p className="text-xs text-gray-500">{formatDate(transfer.driverSettlement?.delivery_date)} • {transfer.payment_method === 'cash' ? '💵 Бэлэн' : '🏦 Шилжүүлэг'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-emerald-600">{formatCurrency(transfer.amount)}</p>
                        <p className="text-xs text-gray-500">{formatDate(transfer.created_at)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* TAB 4: СИСТЕМИЙН ШИМТГЭЛ */}
        {/* ============================================ */}
        {activeTab === 'commission' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Filter */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Хугацаа</label>
                  <select value={commissionDateRange} onChange={(e) => setCommissionDateRange(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm">
                    <option value="today">Өнөөдөр</option>
                    <option value="yesterday">Өчигдөр</option>
                    <option value="week">7 хоног</option>
                    <option value="month">30 хоног</option>
                    <option value="custom">Сонгох</option>
                  </select>
                </div>
                {commissionDateRange === 'custom' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Эхлэх</label>
                      <input type="date" value={commissionCustomStart} onChange={(e) => setCommissionCustomStart(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Дуусах</label>
                      <input type="date" value={commissionCustomEnd} onChange={(e) => setCommissionCustomEnd(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </>
                )}
                <div className="flex items-end">
                  <button onClick={fetchCommissionData} disabled={commissionLoading}
                    className="w-full sm:w-auto px-6 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium">
                    {commissionLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : '🔍'}
                    <span>Хайх</span>
                  </button>
                </div>
              </div>
            </div>

            {commissionLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600"></div>
              </div>
            ) : commissionData ? (
              <>
                {/* Summary Cards - Tab 4 */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 sm:p-5 text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-blue-100 text-xs sm:text-sm">Нийт хүргэлт</p>
                        <p className="text-2xl sm:text-3xl font-bold mt-1">
                          {commissionData.period?.delivered_count || commissionData.today?.delivered_count || 0}
                        </p>
                      </div>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <span className="text-xl">🚚</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 sm:p-5 text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-purple-100 text-xs sm:text-sm">Нэгж шимтгэл</p>
                        <p className="text-xl sm:text-2xl font-bold mt-1 truncate">{formatCurrency(commissionData.company?.commission_value)}</p>
                      </div>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <span className="text-xl">📊</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 sm:p-5 text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-indigo-100 text-xs sm:text-sm">Нийт шимтгэл</p>
                        <p className="text-xl sm:text-2xl font-bold mt-1 truncate">
                          {formatCurrency(commissionData.period?.commission_amount || commissionData.today?.commission_amount || 0)}
                        </p>
                      </div>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <span className="text-xl">💰</span>
                      </div>
                    </div>
                  </div>
                  <div className={`bg-gradient-to-br rounded-xl p-4 sm:p-5 text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer ${
                    commissionData.balance?.balance > 0 ? 'from-orange-500 to-orange-600' : 'from-emerald-500 to-emerald-600'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <p className={`text-xs sm:text-sm ${commissionData.balance?.balance > 0 ? 'text-orange-100' : 'text-emerald-100'}`}>Төлөх үлдэгдэл</p>
                        <p className="text-xl sm:text-2xl font-bold mt-1 truncate">{formatCurrency(commissionData.balance?.balance)}</p>
                      </div>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <span className="text-xl">{commissionData.balance?.balance > 0 ? '⚠️' : '✅'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Alert with Bank Account - Compact Design */}
                {commissionData.balance?.balance > 0 && commissionData.company?.system_bank_account && (
                  <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-4 shadow-md">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      {/* Left - Info */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-lg">💳</span>
                        </div>
                        <div className="text-white">
                          <p className="font-semibold">Төлөх: {formatCurrency(commissionData.balance?.balance)}</p>
                          <p className="text-orange-100 text-xs">Доорх данс руу шилжүүлнэ үү</p>
                        </div>
                      </div>
                      
                      {/* Right - Bank Details */}
                      <div className="bg-white rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                        {/* Bank & Account */}
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-lg">🏦</span>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{commissionData.company.system_bank_account.bank_name}</p>
                            <p className="font-mono font-bold text-gray-900">{commissionData.company.system_bank_account.account_number}</p>
                          </div>
                        </div>
                        
                        {/* Divider & Account Name */}
                        <div className="hidden sm:block w-px h-8 bg-gray-200"></div>
                        <div className="hidden sm:block">
                          <p className="text-xs text-gray-500">Эзэмшигч</p>
                          <p className="font-medium text-gray-900 text-sm">{commissionData.company.system_bank_account.account_name}</p>
                        </div>
                        
                        {/* IBAN */}
                        {commissionData.company.system_bank_account.iban_number && (
                          <>
                            <div className="hidden sm:block w-px h-8 bg-gray-200"></div>
                            <div className="hidden sm:block">
                              <p className="text-xs text-gray-500">IBAN</p>
                              <p className="font-mono font-medium text-gray-900 text-sm">{commissionData.company.system_bank_account.iban_number}</p>
                            </div>
                          </>
                        )}
                        
                        {/* Copy Button */}
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(commissionData.company.system_bank_account.account_number);
                            alert('✅ Хуулагдлаа!');
                          }}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition flex items-center gap-1 self-start sm:self-center"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Хуулах
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* No Bank Account Warning */}
                {commissionData.balance?.balance > 0 && !commissionData.company?.system_bank_account && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">⚠️</span>
                      <p className="text-sm text-yellow-800">Данс тохируулагдаагүй. Системийн админтай холбогдоно уу.</p>
                    </div>
                  </div>
                )}

                {/* All Paid Message */}
                {commissionData.balance?.balance <= 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-sm text-green-800 font-medium">Төлбөр бүрэн төлөгдсөн ✓</p>
                    </div>
                  </div>
                )}

                {/* Transaction History */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">💳 Гүйлгээний түүх</h3>
                    <span className="text-sm text-gray-500">{commissionData.transactions?.length || 0} гүйлгээ</span>
                  </div>
                  <div className="divide-y divide-gray-200 max-h-[400px] overflow-y-auto">
                    {!commissionData.transactions || commissionData.transactions.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-4xl">📭</span>
                          <span>Гүйлгээ байхгүй байна</span>
                        </div>
                      </div>
                    ) : (
                      commissionData.transactions.map((tx) => (
                        <div key={tx.id} className="p-3 sm:p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                              tx.transaction_type === 'payment' ? 'bg-green-100' : 'bg-orange-100'
                            }`}>
                              {tx.transaction_type === 'payment' ? (
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">
                                {tx.transaction_type === 'payment' ? 'Төлбөр хийсэн' : 'Шимтгэл нэмэгдсэн'}
                              </p>
                              <p className="text-xs text-gray-500">{formatDate(tx.created_at)}</p>
                            </div>
                          </div>
                          <span className={`font-semibold text-sm ${tx.transaction_type === 'payment' ? 'text-green-600' : 'text-orange-600'}`}>
                            {tx.transaction_type === 'payment' ? '-' : '+'}{formatCurrency(Math.abs(tx.amount))}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">📭 Мэдээлэл олдсонгүй</div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* MODALS */}
        {/* ============================================ */}

        {/* Shop Detail Modal */}
        {showDetailModal && selectedShopData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{selectedShopData.shop.name}</h2>
                  <p className="text-sm text-gray-500">Төлбөр тооцооны дэлгэрэнгүй</p>
                </div>
                <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-white/50 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
                {detailLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : shopDetail ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500">Нийт өр</p>
                        <p className="text-lg font-bold text-gray-900">{formatCurrency(shopDetail.balance?.total_debt)}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-green-600">Төлсөн</p>
                        <p className="text-lg font-bold text-green-700">{formatCurrency(shopDetail.balance?.total_paid)}</p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-orange-600">Үлдэгдэл</p>
                        <p className="text-lg font-bold text-orange-700">{formatCurrency(shopDetail.balance?.balance)}</p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* Shop Payment Modal */}
        {showPaymentModal && selectedShopData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
                <h2 className="text-lg font-bold text-gray-900">Төлбөр бүртгэх</h2>
                <p className="text-sm text-gray-500">{selectedShopData.shop.name}</p>
              </div>
              <form onSubmit={handleRecordPayment} className="p-4 space-y-4">
                <div className="bg-orange-50 rounded-lg p-3 text-center border border-orange-200">
                  <p className="text-sm text-orange-600">Төлөх үлдэгдэл</p>
                  <p className="text-2xl font-bold text-orange-700">{formatCurrency(selectedShopData.balance.balance)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Дүн *</label>
                  <input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Төлбөрийн хэлбэр</label>
                  <select value={paymentForm.payment_method} onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm">
                    <option value="cash">💵 Бэлэн</option>
                    <option value="bank_transfer">🏦 Шилжүүлэг</option>
                    <option value="qpay">📱 QPay</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowPaymentModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">Болих</button>
                  <button type="submit" disabled={paymentLoading}
                    className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50">
                    {paymentLoading ? 'Хадгалж байна...' : 'Бүртгэх'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* COD Payment Modal */}
        {showCodPaymentModal && selectedCodItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
                <h2 className="text-lg font-bold text-gray-900">COD төлбөр бүртгэх</h2>
                <p className="text-sm text-gray-500">{selectedCodItem.shop?.shop_name} - {formatDate(selectedCodItem.delivery_date)}</p>
              </div>
              <form onSubmit={handleRecordCodPayment} className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-purple-50 rounded-lg p-3 text-center border border-purple-200">
                    <p className="text-sm text-purple-600">Нийт COD</p>
                    <p className="text-xl font-bold text-purple-700">{formatCurrency(selectedCodItem.total_cod)}</p>
                  </div>
                  <div className={`rounded-lg p-3 text-center border ${
                    (selectedCodItem.balance || 0) > 0 
                      ? 'bg-orange-50 border-orange-200' 
                      : 'bg-green-50 border-green-200'
                  }`}>
                    <p className={`text-sm ${(selectedCodItem.balance || 0) > 0 ? 'text-orange-600' : 'text-green-600'}`}>Төлөх үлдэгдэл</p>
                    <p className={`text-xl font-bold ${(selectedCodItem.balance || 0) > 0 ? 'text-orange-700' : 'text-green-700'}`}>
                      {formatCurrency(selectedCodItem.balance || 0)}
                    </p>
                  </div>
                </div>
                
                {(selectedCodItem.balance || 0) <= 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <p className="text-green-700 font-medium">✅ Төлбөр бүрэн төлөгдсөн эсвэл хүлээгдэж байна</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Төлөх дүн *</label>
                  <input 
                    type="number" 
                    value={codPaymentForm.amount} 
                    onChange={(e) => setCodPaymentForm({...codPaymentForm, amount: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:cursor-not-allowed" 
                    required 
                    disabled={(selectedCodItem?.balance || 0) <= 0}
                  />
                </div>
                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Төлбөрийн хэлбэр</label>
                  <select value={codPaymentForm.payment_method} onChange={(e) => setCodPaymentForm({...codPaymentForm, payment_method: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm">
                    <option value="bank_transfer">🏦 Банкны шилжүүлэг</option>
                    <option value="cash">💵 Бэлэн</option>
                    <option value="qpay">📱 QPay</option>
                  </select>
                </div>

                {/* Шопын данс харуулах */}
                {codPaymentForm.payment_method === 'bank_transfer' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-blue-800 mb-2">📥 Шопын хүлээн авах данс:</p>
                    {selectedCodItem.shop?.default_bank_account ? (
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <span className="text-lg">🏦</span>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">{selectedCodItem.shop.default_bank_account.bank_name}</p>
                              <p className="font-mono font-bold text-gray-900">{selectedCodItem.shop.default_bank_account.account_number}</p>
                              <p className="text-sm text-gray-600">{selectedCodItem.shop.default_bank_account.account_name}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(selectedCodItem.shop.default_bank_account.account_number);
                              alert('✅ Дансны дугаар хуулагдлаа!');
                            }}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700"
                          >
                            📋 Хуулах
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                        <p className="text-yellow-700 text-sm">⚠️ Шоп данс бүртгүүлээгүй байна</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowCodPaymentModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">Болих</button>
                  <button 
                    type="submit" 
                    disabled={codPaymentLoading || (selectedCodItem?.balance || 0) <= 0}
                    className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed">
                    {codPaymentLoading ? 'Хадгалж байна...' : 'Бүртгэх'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Driver Transfer Modal */}
        {showDriverTransferModal && selectedDriverItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50">
                <h2 className="text-lg font-bold text-gray-900">Жолоочийн шилжүүлэг</h2>
                <p className="text-sm text-gray-500">{selectedDriverItem.driver_name} - {formatDate(selectedDriverItem.delivery_date)}</p>
              </div>
              <form onSubmit={handleRecordDriverTransfer} className="p-4 space-y-4">
                {/* Статистик картууд */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-sky-50 rounded-lg p-3 text-center border border-sky-200">
                    <p className="text-xs text-sky-600">COD авсан</p>
                    <p className="text-lg font-bold text-sky-700">{formatCurrency(selectedDriverItem.collected_amount || 0)}</p>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-3 text-center border border-indigo-200">
                    <p className="text-xs text-indigo-600">Хүргэлт авсан</p>
                    <p className="text-lg font-bold text-indigo-700">{formatCurrency(selectedDriverItem.collected_delivery_fee || 0)}</p>
                  </div>
                </div>
                
                {/* Үлдэгдэл */}
                <div className="grid grid-cols-2 gap-3">
                  <div className={`rounded-lg p-3 text-center border ${
                    (selectedDriverItem.balance || 0) > 0 ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'
                  }`}>
                    <p className={`text-xs ${(selectedDriverItem.balance || 0) > 0 ? 'text-orange-600' : 'text-green-600'}`}>COD үлдэгдэл</p>
                    <p className={`text-lg font-bold ${(selectedDriverItem.balance || 0) > 0 ? 'text-orange-700' : 'text-green-700'}`}>
                      {formatCurrency(selectedDriverItem.balance || 0)}
                    </p>
                  </div>
                  <div className={`rounded-lg p-3 text-center border ${
                    (selectedDriverItem.delivery_fee_balance || 0) > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'
                  }`}>
                    <p className={`text-xs ${(selectedDriverItem.delivery_fee_balance || 0) > 0 ? 'text-amber-600' : 'text-green-600'}`}>Хүргэлт үлдэгдэл</p>
                    <p className={`text-lg font-bold ${(selectedDriverItem.delivery_fee_balance || 0) > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                      {formatCurrency(selectedDriverItem.delivery_fee_balance || 0)}
                    </p>
                  </div>
                </div>

                {/* Төлбөрийн төрөл сонгох */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Төлбөрийн төрөл *</label>
                  <select 
                    value={driverTransferForm.transfer_type} 
                     onChange={(e) => {
                      const newType = e.target.value;
                      const newBalance = newType === 'cod' 
                        ? (selectedDriverItem.balance || 0)
                        : (selectedDriverItem.delivery_fee_balance || selectedDriverItem.collected_delivery_fee || 0);
                      setDriverTransferForm({
                        ...driverTransferForm, 
                        transfer_type: newType,
                        amount: newBalance > 0 ? newBalance.toString() : ''
                      });
                    }}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="cod">💵 COD төлбөр</option>
                    <option value="delivery_fee">🚚 Хүргэлтийн төлбөр</option>
                  </select>
                </div>

                {/* Сонгосон төрлийн үлдэгдэл */}
                {(() => {
                  const isCod = driverTransferForm.transfer_type === 'cod';
                  const currentBalance = isCod 
                    ? (selectedDriverItem.balance || 0)
                    : (selectedDriverItem.collected_delivery_fee || 0);
                  
                  if (currentBalance <= 0) {
                    return (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                        <p className="text-green-700 font-medium">
                          ✅ {isCod ? 'COD бүрэн шилжүүлэгдсэн' : 'Хүргэлтийн төлбөр бүрэн авсан'}
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {driverTransferForm.transfer_type === 'cod' ? 'Шилжүүлэх дүн' : 'Авах дүн'} *
                  </label>
                  <input 
                    type="number" 
                    value={driverTransferForm.amount} 
                    onChange={(e) => setDriverTransferForm({...driverTransferForm, amount: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" 
                    required 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Шилжүүлгийн хэлбэр</label>
                  <select 
                    value={driverTransferForm.transfer_method} 
                    onChange={(e) => setDriverTransferForm({...driverTransferForm, transfer_method: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="cash">💵 Бэлэн</option>
                    <option value="bank_transfer">🏦 Банкны шилжүүлэг</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <button 
                    type="button" 
                    onClick={() => setShowDriverTransferModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                  >
                    Болих
                  </button>
                  <button 
                    type="submit" 
                    disabled={driverTransferLoading}
                    className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {driverTransferLoading ? 'Хадгалж байна...' : 'Бүртгэх'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </CompanyLayout>
  );
};

export default CompanySettlements;